import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '../lib/api';
import PageSeo from '../components/PageSeo';
import { ChefHat, Refrigerator, Clock, Package, Lightbulb, Users, ArrowLeft, PieChart } from 'lucide-react';

// Simple markdown-like parser for **bold** text
const parseMarkdown = (text) => {
  if (!text) return text;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-[#2A312C]">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export default function ContainersPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const res = await publicApi.getPageContent('contenants-conservation');
      setContent(res.data);
    } catch (error) {
      console.error('Error loading page content:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (type) => {
    switch (type) {
      case 'intro': return <ChefHat className="w-6 h-6" />;
      case 'containers': return <Package className="w-6 h-6" />;
      case 'conservation': return <Refrigerator className="w-6 h-6" />;
      case 'options': return <PieChart className="w-6 h-6" />;
      case 'tips': return <Lightbulb className="w-6 h-6" />;
      default: return <ChefHat className="w-6 h-6" />;
    }
  };

  const renderSection = (section) => {
    switch (section.type) {
      case 'intro':
        return (
          <div key={section.id} className="card-default bg-gradient-to-br from-[#A5BBA8]/10 to-transparent">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#A5BBA8]/20 rounded-xl text-[#A5BBA8]">
                {getSectionIcon(section.type)}
              </div>
              <div>
                <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C] mb-2">
                  {section.title}
                </h2>
                <p className="text-[#5C6A61] leading-relaxed">
                  {parseMarkdown(section.content)}
                </p>
              </div>
            </div>
          </div>
        );

      case 'section':
        return (
          <div key={section.id} className="card-default">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-[#C86B51]/10 rounded-xl text-[#C86B51]">
                <PieChart className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C]">
                  {section.title}
                </h2>
                <p className="text-[#5C6A61] mt-1">{parseMarkdown(section.content)}</p>
              </div>
            </div>
            
            {section.subsections?.map((sub, idx) => (
              <div key={idx} className="ml-14 mt-4 p-4 bg-[#F9F6F0] rounded-xl">
                <h3 className="font-semibold text-[#2A312C] mb-2">{sub.subtitle}</h3>
                <div className="text-[#5C6A61] text-sm space-y-1">
                  {sub.content.split('\\n').map((line, i) => (
                    <p key={i}>{parseMarkdown(line)}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'containers':
        return (
          <div key={section.id} className="card-default">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-[#A5BBA8]/20 rounded-xl text-[#A5BBA8]">
                {getSectionIcon(section.type)}
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C]">
                {section.title}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {section.households?.map((household, idx) => (
                <div 
                  key={idx} 
                  className="p-5 rounded-xl border-2 border-[#E3DED5] hover:border-[#A5BBA8] transition-colors"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#C86B51] rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-[#2A312C] text-lg">{household.size}</h3>
                  </div>
                  <ul className="space-y-2">
                    {household.items?.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#5C6A61]">
                        <span className="text-[#A5BBA8] mt-0.5">•</span>
                        <span>{parseMarkdown(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'conservation':
        return (
          <div key={section.id} className="card-default">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-[#A5BBA8]/20 rounded-xl text-[#A5BBA8]">
                {getSectionIcon(section.type)}
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C]">
                {section.title}
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {section.items?.map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-[#F9F6F0] rounded-lg"
                >
                  <span className="text-sm text-[#2A312C]">{item.name}</span>
                  <span className="text-sm font-medium text-[#C86B51] flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {item.duration}
                  </span>
                </div>
              ))}
            </div>
            
            {section.tips && (
              <div className="p-4 bg-[#E8B25C]/10 border border-[#E8B25C]/30 rounded-xl">
                <p className="font-semibold text-[#E8B25C] text-sm mb-2">Important</p>
                <ul className="space-y-1">
                  {section.tips.map((tip, i) => (
                    <li key={i} className="text-sm text-[#5C6A61] flex items-start gap-2">
                      <span className="text-[#E8B25C]">⚠️</span>
                      <span>{parseMarkdown(tip)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'options':
        return (
          <div key={section.id} className="card-default">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-[#C86B51]/10 rounded-xl text-[#C86B51]">
                {getSectionIcon(section.type)}
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C]">
                {section.title}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              {section.options?.map((option, idx) => (
                <div key={idx} className="p-4 bg-[#F9F6F0] rounded-xl">
                  <h3 className="font-semibold text-[#2A312C] mb-3 pb-2 border-b border-[#E3DED5]">
                    {option.name}
                  </h3>
                  <ul className="space-y-2">
                    {option.items?.map((item, i) => (
                      <li key={i} className="text-sm text-[#5C6A61] flex items-start gap-2">
                        <span className="text-[#A5BBA8]">•</span>
                        <span>{parseMarkdown(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'tips':
        return (
          <div key={section.id} className="card-default bg-gradient-to-br from-[#A5BBA8]/5 to-transparent">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-[#A5BBA8]/20 rounded-xl text-[#A5BBA8]">
                {getSectionIcon(section.type)}
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#2A312C]">
                {section.title}
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-3 ml-14">
              {section.tips?.map((tip, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#E3DED5]"
                >
                  <span className="text-[#A5BBA8] text-lg">💡</span>
                  <span className="text-sm text-[#5C6A61]">{parseMarkdown(tip)}</span>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <div className="text-[#5C6A61]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <PageSeo title={content?.seo_title} description={content?.meta_description} />
      {/* Header */}
      <header className="bg-white border-b border-[#E3DED5]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </Link>
          <Link to="/" className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2A312C]">
            Mitonner
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#A5BBA8]/20 to-[#F9F6F0] py-12 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2A312C] mb-4">
            {content?.title || 'Contenants et conservation'}
          </h1>
          <p className="text-[#5C6A61] max-w-2xl mx-auto">
            Tout ce qu'il faut savoir pour bien préparer votre intervention et conserver vos plats
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto space-y-6">
          {content?.sections?.map(section => renderSection(section))}
        </div>
      </main>

      {/* CTA */}
      <section className="bg-[#2A312C] py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-white mb-4">
            Prêt à réserver ?
          </h2>
          <p className="text-[#A5BBA8] mb-6">
            Découvrez nos formules et composez vos menus de la semaine
          </p>
          <Link 
            to="/client/reservation" 
            className="inline-flex items-center justify-center px-6 py-3 bg-[#C86B51] text-white rounded-xl font-semibold hover:bg-[#B35A42] transition-colors"
          >
            Réserver une prestation
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A312C] border-t border-[#5C6A61]/20 py-6">
        <div className="container mx-auto px-4 text-center text-[#5C6A61] text-sm">
          © {new Date().getFullYear()} Mitonner. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
